
import { RouteObject } from 'react-router-dom';

// Layouts
import MainLayout from '@/layouts/MainLayout';
import AuthLayout from '@/layouts/AuthLayout';

// Protected route component
import ProtectedRoute from '@/components/ProtectedRoute';

// Direct imports instead of lazy loading to avoid React error #306
import Dashboard from '@/pages/Dashboard';
import Login from '@/pages/Login';
import Profile from '@/pages/Profile';
import NotFound from '@/pages/NotFound';

// Sales components
import SalesWorkflow from '@/features/sales/SalesWorkflow';
import CheckoutFlow from '@/features/sales/CheckoutFlow';
import SalesOrderBooking from '@/features/sales/SalesOrderBooking';
import PaymentProcessing from '@/features/sales/PaymentProcessing';

// Customer components
import CustomerList from '@/features/customer/CustomerList';
import CustomerDashboard from '@/features/customer/CustomerDashboard';
import CustomerOrders from '@/features/customer/CustomerOrders';
import CustomerProfile from '@/features/customer/CustomerProfile';
import CustomerWarranty from '@/features/customer/CustomerWarranty';

// Billing components
import CreateInvoice from '@/features/billing/CreateInvoice';
import InvoiceList from '@/features/billing/InvoiceList';
import InvoiceDetail from '@/features/billing/InvoiceDetail';
import RateFixation from '@/features/billing/RateFixation';
import PaymentPage from '@/features/billing/PaymentPage';

// Inventory components
import InventoryList from '@/features/inventory/InventoryList';
import OldGoldPurchase from '@/features/inventory/OldGoldPurchase';
import TransferStock from '@/features/inventory/TransferStock';
import VendorList from '@/features/inventory/VendorList';

// QC components
import QCDashboard from '@/features/qc/QCDashboard';
import QCOrdersList from '@/features/qc/QCOrdersList';
import QCInspectionDetail from '@/features/qc/QCInspectionDetail';
import ChecklistManager from '@/features/qc/ChecklistManager';

// Orders components
import OrderFulfillment from '@/features/orders/OrderFulfillment';

// Operations components
import DiscountCategoryManager from '@/features/operations/DiscountCategoryManager';
import VendorChangeApproval from '@/features/operations/VendorChangeApproval';
import DiscountProposalManager from '@/features/operations/DiscountProposalManager';

// HO Manager components
import MarginFixation from '@/features/ho-manager/MarginFixation';

// Notification components
import NotificationCenter from '@/features/notifications/NotificationCenter';

// Todo components
import TodoList from '@/features/todo/TodoList';

// Report components
import MonthlyFinanceReports from '@/features/reports/MonthlyFinanceReports';
import GSTReports from '@/features/reports/GSTReports';
import AuditReports from '@/features/reports/AuditReports';
import ComplianceReports from '@/features/reports/ComplianceReports';
import BranchAnalytics from '@/features/reports/BranchAnalytics';
import SEReports from '@/features/reports/SEReports';
import SupplierPerformance from '@/features/reports/SupplierPerformance';
import ScheduledReports from '@/features/reports/ScheduledReports';

// Supplier components
import SupplierList from '@/features/suppliers/SupplierList';
import SupplierOrders from '@/features/suppliers/SupplierOrders';
import SupplierLedger from '@/features/suppliers/SupplierLedger';

// Vendor components
import VendorEnroll from '@/features/vendor/VendorEnroll';
import VendorDetail from '@/features/vendor/VendorDetail';
import VendorListPage from '@/features/vendor/VendorList';

// Warranty components
import WarrantyClaims from '@/features/warranty/WarrantyClaims';
import RepairOrders from '@/features/warranty/RepairOrders';
import ReplacementOrders from '@/features/warranty/ReplacementOrders';
import WarrantyReports from '@/features/warranty/WarrantyReports';

// Product components
import ProductListing from '@/features/products/ProductListing';
import ProductDetail from '@/pages/ProductDetail';

// Executive components
import CEODashboard from '@/features/ceo/CEODashboard';
import DirectorDashboard from '@/features/director/DirectorDashboard';

// Admin components
import SystemAdmin from '@/features/admin/SystemAdmin';

// Portal components
import CustomerPortal from '@/features/customer/CustomerPortal';
import VendorPortal from '@/features/vendor/VendorPortal';

const routes: RouteObject[] = [
  {
    path: '/login',
    element: <AuthLayout />,
    children: [
      {
        index: true,
        element: <Login />
      }
    ]
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Dashboard />
      },
      {
        path: 'profile',
        element: <Profile />
      },
      // Sales routes
      {
        path: 'sales/workflow',
        element: <SalesWorkflow />
      },
      {
        path: 'sales/checkout',
        element: <CheckoutFlow />
      },
      {
        path: 'sales/orders',
        element: <SalesOrderBooking />
      },
      {
        path: 'sales/payment',
        element: <PaymentProcessing />
      },
      // Customer routes
      {
        path: 'customers',
        element: <CustomerList />
      },
      {
        path: 'customer/list',
        element: <CustomerList />
      },
      {
        path: 'customers/dashboard',
        element: <CustomerDashboard />
      },
      {
        path: 'customers/orders',
        element: <CustomerOrders />
      },
      {
        path: 'customers/profile',
        element: <CustomerProfile />
      },
      {
        path: 'customers/warranty',
        element: <CustomerWarranty />
      },
      // Billing routes
      {
        path: 'billing/create',
        element: <CreateInvoice />
      },
      {
        path: 'billing/invoices',
        element: <InvoiceList />
      },
      {
        path: 'billing/invoice/:id',
        element: <InvoiceDetail />
      },
      {
        path: 'billing/rates',
        element: <RateFixation />
      },
      {
        path: 'billing/payment',
        element: <PaymentPage />
      },
      // Inventory routes
      {
        path: 'inventory',
        element: <InventoryList />
      },
      {
        path: 'inventory/old-gold',
        element: <OldGoldPurchase />
      },
      {
        path: 'inventory/transfer',
        element: <TransferStock />
      },
      {
        path: 'inventory/vendors',
        element: <VendorList />
      },
      // Product routes
      {
        path: 'products',
        element: <ProductListing />
      },
      {
        path: 'products/:id',
        element: <ProductDetail />
      },
      // QC routes
      {
        path: 'qc',
        element: <QCDashboard />
      },
      {
        path: 'qc/orders',
        element: <QCOrdersList />
      },
      {
        path: 'qc/inspection/:id',
        element: <QCInspectionDetail />
      },
      {
        path: 'qc/checklists',
        element: <ChecklistManager />
      },
      // Order routes
      {
        path: 'orders/fulfillment',
        element: <OrderFulfillment />
      },
      // Operations Head routes
      {
        path: 'operations/discount-categories',
        element: <DiscountCategoryManager />
      },
      {
        path: 'operations/vendor-changes',
        element: <VendorChangeApproval />
      },
      {
        path: 'operations/discount-proposals',
        element: <DiscountProposalManager />
      },
      // CEO routes
      {
        path: 'ceo/dashboard',
        element: <CEODashboard />
      },
      // Director routes
      {
        path: 'director/dashboard',
        element: <DirectorDashboard />
      },
      // System Admin routes
      {
        path: 'admin/system',
        element: <SystemAdmin />
      },
      // Customer Portal routes
      {
        path: 'customer/portal',
        element: <CustomerPortal />
      },
      // Vendor Portal routes
      {
        path: 'vendor/portal',
        element: <VendorPortal />
      },
      // Vendor routes
      {
        path: 'vendor/list',
        element: <VendorListPage />
      },
      {
        path: 'vendors/enroll',
        element: <VendorEnroll />
      },
      {
        path: 'vendors/:id',
        element: <VendorDetail />
      },
      // Notification routes
      {
        path: 'notifications',
        element: <NotificationCenter />
      },
      // Todo routes
      {
        path: 'todo',
        element: <TodoList />
      },
      // Report routes
      {
        path: 'reports/finance',
        element: <MonthlyFinanceReports />
      },
      {
        path: 'reports/gst',
        element: <GSTReports />
      },
      {
        path: 'reports/audit',
        element: <AuditReports />
      },
      {
        path: 'reports/compliance',
        element: <ComplianceReports />
      },
      {
        path: 'reports/branch',
        element: <BranchAnalytics />
      },
      {
        path: 'reports/se',
        element: <SEReports />
      },
      {
        path: 'reports/supplier',
        element: <SupplierPerformance />
      },
      {
        path: 'reports/scheduled',
        element: <ScheduledReports />
      },
      // Supplier routes
      {
        path: 'suppliers',
        element: <SupplierList />
      },
      {
        path: 'suppliers/orders',
        element: <SupplierOrders />
      },
      {
        path: 'suppliers/ledger',
        element: <SupplierLedger />
      },
      // Warranty routes
      {
        path: 'warranty/claims',
        element: <WarrantyClaims />
      },
      {
        path: 'warranty/repairs',
        element: <RepairOrders />
      },
      {
        path: 'warranty/replacements',
        element: <ReplacementOrders />
      },
      {
        path: 'warranty/reports',
        element: <WarrantyReports />
      },
      // HO Manager routes
      {
        path: 'ho-manager/dashboard',
        element: <Dashboard />
      },
      {
        path: 'ho-manager/products',
        element: <MarginFixation />
      },
      {
        path: 'ho-manager/orders',
        element: <OrderFulfillment />
      },
      {
        path: 'ho-manager/rates',
        element: <RateFixation />
      },
      {
        path: 'ho-manager/vendors',
        element: <VendorListPage />
      }
    ]
  },
  {
    path: '*',
    element: <NotFound />
  }
];

export default routes;
